// src/pages/PendingReview.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { postsAPI } from '../utils/api';
import { getPlatform } from '../utils/platforms';
import { format } from 'date-fns';

export default function PendingReview() {
  const navigate = useNavigate();
  const { posts, postsTotal, postsLoading, fetchPosts, deletePost, fetchSummary } = useStore();
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // post id being actioned

  useEffect(() => { fetchPosts({ status: 'pending_review' }); }, []);

  const handleApprove = async (post) => {
    setActionLoading(post.id);
    try {
      await postsAPI.update(post.id, {
        caption: post.caption,
        scheduled_at: post.scheduled_at,
        status: 'queued',
        tags: post.tags || [],
        media_ids: post.media_ids || [],
      });
      toast.success('Post approved and queued');
      fetchPosts({ status: 'pending_review' });
      fetchSummary();
    } catch {
      toast.error('Failed to approve post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestChanges = async (post) => {
    setActionLoading(`reject-${post.id}`);
    try {
      await postsAPI.update(post.id, {
        caption: post.caption,
        scheduled_at: post.scheduled_at,
        status: 'draft',
        tags: post.tags || [],
        media_ids: post.media_ids || [],
      });
      toast.success('Post sent back to drafts');
      fetchPosts({ status: 'pending_review' });
      fetchSummary();
    } catch {
      toast.error('Failed to update post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await deletePost(id);
    toast.success('Post deleted');
  };

  const filtered = posts.filter(p => !search || p.caption?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <div>
          <span className="page-title">Pending Review</span>
          <div className="page-subtitle">Posts awaiting approval before publishing</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/posts/create')}>+ Create Post</button>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <span style={{ color: '#aaa' }}>🔍</span>
          <input placeholder="Search pending posts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <span className="count-text ml-auto">{filtered.length} of {postsTotal} pending</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {postsLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <span style={{ fontSize: 40 }}>✅</span>
            <h3>Nothing to review</h3>
            <p>Posts submitted for review will appear here</p>
          </div>
        ) : (
          filtered.map(p => {
            const accData = p.accounts?.filter(a => a && a.platform) || [];
            const isActioning = actionLoading === p.id || actionLoading === `reject-${p.id}`;
            return (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 72, minHeight: 80, background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>👁</div>
                  <div style={{ flex: 1, padding: '10px 12px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#f4a118', textTransform: 'uppercase', letterSpacing: '.05em' }}>Pending Review</span>
                      {p.scheduled_at && (
                        <span style={{ fontSize: 10, color: '#0f766e' }}>
                          📅 {format(new Date(p.scheduled_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#1a1f2e', lineHeight: 1.6, marginBottom: 6 }}>
                      {p.caption || <span style={{ color: '#aaa', fontStyle: 'italic' }}>No caption</span>}
                    </div>
                    {p.tags?.length > 0 && <div style={{ fontSize: 10, color: '#0f766e' }}>{p.tags.join(' ')}</div>}
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                      Submitted {format(new Date(p.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, minWidth: 140, borderLeft: '1px solid #f0f1f5' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-end' }}>
                      {accData.slice(0, 4).map((a, i) => { const pl = getPlatform(a.platform); return <div key={i} className="platform-icon" style={{ background: pl.bg, color: pl.color, width: 18, height: 18 }}>{pl.icon}</div>; })}
                      {accData.length === 0 && <span style={{ fontSize: 10, color: '#aaa' }}>No accounts</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>
                      {accData.length} account{accData.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div style={{ padding: '10px 12px', borderTop: '1px solid #f5f6fa', display: 'flex', alignItems: 'center', gap: 8, background: '#fafbfd' }}>
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 11, padding: '5px 12px' }}
                    onClick={() => handleApprove(p)}
                    disabled={isActioning}
                  >
                    {actionLoading === p.id ? '…' : '✓ Approve'}
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: 11, padding: '5px 12px' }}
                    onClick={() => handleRequestChanges(p)}
                    disabled={isActioning}
                  >
                    {actionLoading === `reject-${p.id}` ? '…' : '↩ Request Changes'}
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: 11, padding: '5px 10px', marginLeft: 'auto' }}
                    onClick={() => navigate(`/posts/edit/${p.id}`)}
                  >✏ Edit</button>
                  <button
                    className="btn btn-danger"
                    style={{ fontSize: 11, padding: '5px 10px' }}
                    onClick={() => handleDelete(p.id)}
                  >🗑</button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
